import { Button, Group, Modal, Stack } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useState } from 'react';
import { z } from 'zod';

import { Declaration } from '@e-origin/shared';

import { UploadFile } from '@components';
import { useManuallySetScreenshot } from '@hooks/declaration';

interface GoodsItemUploadScreenshotProps {
  opened: boolean;
  afterCreate?(): void;
  onHide(): void;
  url: string;
  goodDetailsId: string;
  declaration: Declaration;
}

const screenshotSchema = z.object({
  file: z.instanceof(File, { message: 'File is required' }),
});

type ScreenshotFormValues = z.infer<typeof screenshotSchema>;

export const GoodsItemUploadScreenshot: React.FC<GoodsItemUploadScreenshotProps> = (props) => {
  const { onHide, afterCreate, url, goodDetailsId, declaration } = props;
  const [submitting, setSubmitting] = useState(false);

  const manuallySetScreenshotMutation = useManuallySetScreenshot();

  const form = useForm<ScreenshotFormValues>({
    initialValues: {
      file: null,
    },
    validate: zodResolver(screenshotSchema),
    validateInputOnBlur: true,
  });

  const onSubmit = async (values: ScreenshotFormValues) => {
    setSubmitting(true);

    await manuallySetScreenshotMutation.mutateAsync({
      id: declaration._id,
      goodItemId: goodDetailsId,
      file: values.file,
    });

    setSubmitting(false);
    afterCreate?.();
    onHide();
  };

  return (
    <Modal title="Upload Screenshot" opened={props.opened} onClose={props.onHide} size="lg" zIndex={9999} centered>
      <form onSubmit={form.onSubmit(onSubmit)}>
        <Stack gap="sm" mb="xl">
          {url && (
            <Button component="a" href={url} target="_blank" rel="noopener noreferrer" variant="filled" size="lg">
              Open link
            </Button>
          )}

          <UploadFile {...form.getInputProps('file')} isMulti={false} type="image" p="xs" />
        </Stack>

        <Group justify="center">
          <Button type="button" onClick={onHide} disabled={submitting} variant="transparent" size="lg">
            Cancel
          </Button>

          <Button type="submit" loading={submitting} disabled={!form.isValid()} variant="filled" size="lg">
            Submit
          </Button>
        </Group>
      </form>
    </Modal>
  );
};
